/**
 * ComingSoon — an honest placeholder for menu items whose screens aren't built.
 *
 * The nav shows the full product IA so its shape is visible; the unbuilt entries
 * land here rather than 404. Each says plainly what the screen will do and HOW it
 * integrates (which southbound — uCentral config push vs owgw jobs/streams), so
 * the page doubles as an in-product roadmap. Nothing here fakes functionality.
 */
import { useLocation } from "react-router-dom";
import { PageHeader } from "@/components/ui/Page";

interface Meta {
  title: string;
  summary: string;
  /** How it will integrate (the honest engineering note). */
  integration: string;
  /** Concrete capabilities the screen will expose. */
  bullets: string[];
}

// Keyed by the route section segment (last meaningful path part).
const SECTION_META: Record<string, Meta> = {
  /* ---- Gateway (site edge) -------------------------------------------------------------
   * Every entry below was checked against the LIVE QOTOM gateway (207c14f09d7f) on
   * 2026-09-12, not against the uCentral schema or a wish list. Its config carries
   * `interfaces`, `services`, `config-raw`, `uuid`; its state carries `interfaces`,
   * `link-state`, `lldp-peers`, `unit`, `version`; and it advertises `supported-features:
   * null`. Where a screen says "the device does not report this", that is a measurement.
   */
  // NOTE: "security-center" is no longer a ComingSoon entry — it is a built, read-only screen
  // (routes/GatewaySecurity, "Security Posture") that aggregates the firewall/routes/watchdog
  // reads. The IDS/IPS event-feed idea it used to describe remains a firmware ask, not a screen.
  "gateway-dhcp": {
    title: "Gateway DHCP",
    summary: "DHCP scopes the gateway serves, plus static reservations and current leases.",
    integration:
      "Built on the existing gateway config write path (services/gateway_config) as `dhcp.*` intents. The uCentral interface schema carries `ipv4.dhcp` (lease-first, lease-count, lease-time, use-dns) and, as a sibling inside `ipv4`, `ipv4.dhcp-leases[]` for reservations. Two honest caveats. Reservations are accepted by the schema but NOT applied by TIP v5.1.0-rc2 firmware (two renderer defects), so the screen says so on every one. And the gateway CAN report active leases in its state document (`ipv4.leases[]`), but only when the client also appears in its bridge table with a learned address — it has never been observed populated on our hardware, so the lease panel is best-effort and labelled as such, never a stand-in for the configured scope.",
    bullets: [
      "Scopes and reservations: `dhcp.*` intents on the existing gateway write path",
      "Reservations: schema-valid, not applied by TIP v5.1.0-rc2 — a firmware fix",
      "Active leases: reported only when a client is in the bridge table; never yet observed",
    ],
  },
  "gateway-routing": {
    title: "Routing",
    summary: "Static routes and route preference on the site edge.",
    integration:
      "A FIRMWARE ASK, not an editor job. The uCentral schema on these gateways has no static-route object at all — `schemareader.uc` contains zero `route` matches — so there is nothing in the config for a screen to write. The only routing knobs that exist are an interface's `ipv4.gateway` and `metric`, and those sit on the upstream interface this controller reaches the box through, which is the single easiest way to strand a site. Static routes need adding to the schema first; bundle that with the Route Table state request, since one is the write side of the other.",
    bullets: [
      "No static-route object in the uCentral schema — requires a firmware change",
      "Bundle with the Route Table (`Routing-IPv4-Nexthop`) state request",
      "Upstream metric/gateway edits need a pre-flight that proves the WAN survives",
    ],
  },
  "traffic-shaping": {
    title: "Traffic Shaping",
    summary: "Bandwidth limits and per-application priority at the site edge.",
    integration:
      "PARTLY REAL, and the distinction matters. Per-SSID rate limits exist today and are live — they are under Wireless → SSID Firewall & Rate Limits, and they are enforced by the AP. What does not exist is shaping at the GATEWAY: no queueing, policy or per-application classification appears in the whitebox gateway's configuration. Per-application shaping additionally needs DPI, which nothing in this stack currently performs.",
    bullets: [
      "Per-SSID rate limits: shipped, under Wireless",
      "Gateway-level shaping: not in the device's config",
      "Per-application priority additionally needs DPI — not present",
    ],
  },
  "threat-protection": {
    title: "Threat Protection",
    summary: "IDS/IPS and malware blocking on traffic crossing the gateway.",
    integration:
      "Same gap as Security Center, and the same answer: there is no engine on the device to produce this. It is a firmware and licensing question for the gateway platform, not UI work, and it is worth being straight with customers that this is where a Meraki MX comparison genuinely favours Meraki today.",
    bullets: [
      "Needs a device-side IDS/IPS in the gateway image",
      "A real competitive gap, not a missing screen",
      "Raise with Edgecore alongside the other firmware asks",
    ],
  },
  // "content-filtering" is BUILT (DNS-layer, via routes/GatewayContentFiltering) and no longer
  // lands here. TRUE category/URL content filtering still needs a categorisation feed and
  // device-side enforcement — not yet; the built screen states that limit in its own copy.
  "gateway-access-control": {
    title: "Gateway Access Control",
    summary: "Which clients and groups may reach what, enforced at the site edge.",
    integration:
      "Distinct from Wireless → Wi-Fi Access Lists, which is shipped and enforces at the AP for wireless clients. Gateway-level policy would cover everything behind the edge including wired clients, and the gateway's configuration carries no policy section today. Deliberately NOT linked to the wireless screen: pointing this entry at a different enforcement point would quietly misrepresent where a rule applies.",
    bullets: [
      "Wireless access control is shipped — a different enforcement point",
      "Gateway config has no policy section today",
      "Wired-client policy is the actual gap",
    ],
  },

  topology: {
    title: "Topology",
    summary: "A live map of how devices connect — APs to their upstream switch/port, drawn from LLDP.",
    integration: "Built from data we already collect (owgw LLDP + the switch-side SNMP poll). No new southbound.",
    bullets: [
      "AP → switch → port graph from LLDP neighbors",
      "Link speed and VLAN on each edge",
      "Click a node to open its device detail",
    ],
  },
  ssids: {
    title: "Wi-Fi (SSIDs)",
    summary: "Create and edit the wireless networks a site broadcasts.",
    integration: "uCentral config push (controller → owgw → AP), site-scoped, with preview + confirm + audit — the same path as the Configure actions. This is the next screen being built.",
    bullets: [
      "Name, hidden/broadcast, VLAN tag, band (2.4/5/6 GHz)",
      "Security: WPA2/WPA3, PSK (write-only, never revealed)",
      "Enterprise 802.1X / RADIUS, client isolation",
    ],
  },
  "access-control": {
    title: "Wi-Fi Access Lists",
    summary: "Who and how devices authenticate onto each SSID.",
    integration: "uCentral `ssids[].encryption` / `radius` blocks, pushed via owgw. RADIUS server refs stored as secrets (Vault).",
    bullets: [
      "802.1X enterprise auth to an external RADIUS",
      "Per-user/per-device keys (IPSK/DPSK)",
      "MAC allow/deny lists",
    ],
  },
  firewall: {
    title: "SSID Firewall & Rate Limits",
    summary: "Local L3/L4 rules and per-client/SSID bandwidth limits at the AP.",
    integration: "uCentral `firewall` + `rate-limit` blocks (we have the catalog stub), pushed via owgw.",
    bullets: [
      "Block/allow local subnet, ports, peer-to-peer",
      "Up/down bandwidth caps per SSID or client",
      "Traffic shaping rules",
    ],
  },
  radio: {
    title: "Radio & RF",
    summary: "Channel plan, transmit power, channel width and data-rate tuning.",
    integration: "uCentral `radios[]` block, site- or device-scoped, pushed via owgw.",
    bullets: [
      "Channel: auto (RRM) or static, per band",
      "Transmit power: auto or manual",
      "Channel width (20/40/80/160), minimum bitrate",
    ],
  },
  "rf-spectrum": {
    title: "RF Spectrum",
    summary: "Live radio-frequency view — channel utilization, interference, and noise per AP.",
    integration: "Read from owgw telemetry (uCentral `wifiscan` / channel stats); a monitoring view, no config push.",
    bullets: [
      "Per-channel utilization and noise floor",
      "Non-Wi-Fi interference sources",
      "Neighboring-AP / rogue overlap",
    ],
  },
  "switch-portprofiles": {
    title: "Switch Port Profiles",
    summary: "Reusable named port configurations applied across switch ports and sites.",
    integration: "A catalog Template bound to switch ports, pushed via the switch southbound (owgw for OpenWiFi switches; SNMP/CLI for stock).",
    bullets: [
      "Named access/trunk profiles (VLANs, PoE, speed)",
      "Apply a profile to many ports at once",
      "Site-wide reuse with drift detection",
    ],
  },
  vouchers: {
    title: "Vouchers",
    summary: "Generate, print and track guest Wi-Fi access codes (time- or data-bound).",
    integration: "A controller-side voucher store + a captive-portal auth backend (local credentials or RADIUS). Pairs with Guest Portal.",
    bullets: ["Batch-generate codes with duration/data caps", "Print/export vouchers", "Redemption + usage tracking"],
  },
  pms: {
    title: "PMS Integration",
    summary: "Authenticate guests against the hotel PMS (room + name) for Wi-Fi access.",
    integration: "A pluggable PMS connector (Oracle OPERA, Protel, ...) feeding the captive portal; creds in Vault, write-only.",
    bullets: ["Room/name verification at the splash", "Connector interface, one PMS first", "Charge-to-room hooks (later)"],
  },
  "walled-gardens": {
    title: "Walled Gardens",
    summary: "Hosts a guest can reach BEFORE authenticating (payment, ads, hotel site).",
    integration: "uCentral captive `walled-garden-fqdn` — already editable inside Guest Portal; this is the dedicated management view.",
    bullets: ["Allow-list FQDNs/IPs pre-auth", "Per-portal or shared lists", "Redirect to an external/marketing portal"],
  },
  "switch-ports": {
    title: "Switch Ports",
    summary: "Per-port status and configuration for managed switches.",
    integration: "For an OpenWiFi switch: uCentral `ethernet`/`port` blocks via owgw. For a stock switch (e.g. the ECS2100): a multi-vendor SNMP/CLI southbound (read status now; config later).",
    bullets: [
      "Live link state, speed/duplex, and LLDP neighbor per port",
      "Enable/disable a port, set access/trunk VLANs",
      "Port descriptions and speed/duplex overrides",
    ],
  },
  "switch-vlans": {
    title: "VLAN Library",
    summary: "The VLANs a switch carries and how they map to ports.",
    integration: "OpenWiFi switch: uCentral config push via owgw. Stock switch: SNMP dot1q read + CLI/REST config (multi-vendor southbound).",
    bullets: [
      "Create/edit VLANs and names",
      "Assign access VLAN or trunk membership per port",
      "Native/untagged VLAN per trunk",
    ],
  },
  "switch-poe": {
    title: "PoE",
    summary: "Power-over-Ethernet budget and per-port delivery.",
    integration: "Read PoE draw from the switch (SNMP POWER-ETHERNET-MIB / uCentral state); per-port enable/priority via the switch southbound.",
    bullets: [
      "Total budget and per-port watts delivered",
      "Enable/disable PoE and set priority per port",
      "Power-cycle a PoE port (remote reboot of an AP)",
    ],
  },
  firmware: {
    title: "Firmware",
    summary: "Manage device firmware across the fleet with control and safety.",
    integration: "owgw upgrade jobs + a scheduler (a new orchestration layer — the larger 'Tier B' initiative). Not a config push.",
    bullets: [
      "Scheduled maintenance windows, staged rollouts",
      "Automatic rollback on a failed upgrade",
      "Version lock + firmware release notes",
    ],
  },
};

export default function ComingSoon() {
  const { pathname } = useLocation();
  // The section is the last path segment (e.g. /o/1/s/2/ssids -> "ssids").
  const section = pathname.split("/").filter(Boolean).pop() ?? "";
  const meta = SECTION_META[section];

  if (!meta) {
    return (
      <div>
        <PageHeader title="Coming soon" subtitle="This screen isn't built yet." />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <PageHeader title={meta.title} subtitle={meta.summary} />
        <span className="mt-1 rounded-full border border-line px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-ink-subtle">
          Coming soon
        </span>
      </div>

      <div className="mt-4 grid max-w-3xl gap-4 sm:grid-cols-2">
        <section className="md-card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            What it will do
          </h3>
          <ul className="flex flex-col gap-1.5 text-sm text-ink">
            {meta.bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <span className="text-brand">•</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="md-card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            How it integrates
          </h3>
          <p className="text-sm text-ink-muted">{meta.integration}</p>
        </section>
      </div>
    </div>
  );
}
